import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../../services/firebase.config";
import { gradientColors, White08 } from "../theme/colors";
// Overview moved out to be rendered in Profile.js
import { useAuth } from "../../services/AuthContext";
import dataCache from "../../services/DataCache";
import mixpanelService from "../../services/MixpanelService";
import postHogService from "../../services/PostHogService";
import remoteQuizService from "../../services/RemoteQuizService";

import {
  SummaryContainer,
  SummaryHeader,
  TabContainer,
  Tab,
  TabContent,
  TraitRow,
  LoadingRow,
  QuizzesGrid,
  QuizCardBorder,
  QuizCardInner,
  QuizCardTitle,
  SliderContainer,
  SliderTitle,
  SliderTrack,
  SliderFill,
  SliderLabels,
  SliderLabel,
} from "./ProfileStyles";
import { P2, P3 } from "../theme/text/Headings";

// Trait configuration with labels and slider properties
const traitConfig = {
  socialEnergy: {
    title: "Social Energy",
    leftLabel: "Introvert",
    rightLabel: "Extrovert",
  },
  decisionDriver: {
    title: "Decision Driver",
    leftLabel: "Facts",
    rightLabel: "Feeling",
  },
  mindset: {
    title: "Mindset",
    leftLabel: "Concrete",
    rightLabel: "Abstract",
  },
  attachmentStyle: {
    title: "Attachment Style",
    leftLabel: "Anxious",
    rightLabel: "Avoidant",
  },
  politics: {
    title: "Politics",
    leftLabel: "Conservative",
    rightLabel: "Liberal",
  },
  connectionType: {
    title: "Connection Type",
    leftLabel: "Casual",
    rightLabel: "Romantic",
  },
  structure: {
    title: "Structure",
    leftLabel: "Spontaneous",
    rightLabel: "Planner",
  },
  lifestyle: {
    title: "Lifestyle",
    leftLabel: "Thrifty",
    rightLabel: "Boujee",
  },
};

const Summary = ({
  userId,
  isPublicProfile = false,
  onRegenerateRef,
  onRegenerateDataChange,
  profileInsightsRef: externalProfileInsightsRef,
}) => {
  const navigate = useNavigate();
  const { currentUserState } = useAuth();

  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("summary");
  const [completedQuizCount, setCompletedQuizCount] = useState(0);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [availableQuizzes, setAvailableQuizzes] = useState([]);
  const [mergedQuizConfigs, setMergedQuizConfigs] = useState({});

  // Ref for ProfileInsights component to trigger regeneration (can be provided externally)
  const internalProfileInsightsRef = useRef(null);
  const profileInsightsRef =
    externalProfileInsightsRef || internalProfileInsightsRef;

  // Overview is now rendered in Profile.js using this ref

  // Function to check if a shared quiz already exists between current user and profile user
  const checkExistingSharedQuiz = async (quizType, profileUserQuizId) => {
    console.log("🔍 checkExistingSharedQuiz called:", {
      quizType,
      profileUserQuizId,
      currentUser: currentUserState?.uid,
      profileUser: userId,
    });

    if (!currentUserState?.uid || !profileUserQuizId) {
      console.log("❌ Missing current user or profile user quiz ID");
      return null;
    }

    try {
      // OPTIMIZED: Check cache for current user profile
      let currentUserData = dataCache.getUserProfile(currentUserState.uid);

      if (!currentUserData) {
        const currentUserProfileRef = doc(
          db,
          "public-profiles",
          currentUserState.uid
        );
        const currentUserProfileDoc = await getDoc(currentUserProfileRef);

        if (!currentUserProfileDoc.exists()) {
          console.log("❌ Current user profile doesn't exist");
          return null;
        }

        currentUserData = currentUserProfileDoc.data();
        // Cache for future use
        dataCache.setUserProfile(currentUserState.uid, currentUserData);
      }
      const config = mergedQuizConfigs[quizType];
      const currentUserQuizId = currentUserData[config.databaseField];
      console.log("🔍 Current user quiz data:", {
        databaseField: config.databaseField,
        currentUserQuizId,
      });

      if (!currentUserQuizId) {
        console.log("❌ Current user hasn't taken this quiz");
        return null;
      }

      // OPTIMIZED: Check cache for current user's quiz completion
      const currentUserQuizCacheKey = `quiz-data-${currentUserQuizId}`;
      let currentUserQuizData = dataCache.get(currentUserQuizCacheKey);

      if (!currentUserQuizData) {
        const currentUserQuizRef = doc(db, "quiz-answers", currentUserQuizId);
        const currentUserQuizDoc = await getDoc(currentUserQuizRef);
        if (currentUserQuizDoc.exists()) {
          currentUserQuizData = currentUserQuizDoc.data();
          // Cache quiz data for 10 minutes
          dataCache.set(
            currentUserQuizCacheKey,
            currentUserQuizData,
            10 * 60 * 1000
          );
        }
      }

      if (!currentUserQuizData || currentUserQuizData.status !== "completed") {
        console.log("❌ Current user hasn't completed this quiz");
        return null;
      }

      console.log(
        "✅ Current user has completed quiz, searching for shared quizzes..."
      );

      // Search for existing shared quiz between these two users for this quiz type
      // Query by sender/receiver to satisfy security rules, then filter by quiz details
      const sharedQuizzesQuery1 = query(
        collection(db, "shared-quizzes"),
        where("senderUid", "==", currentUserState.uid),
        where("receiverUid", "==", userId)
      );

      const sharedQuizzesQuery2 = query(
        collection(db, "shared-quizzes"),
        where("senderUid", "==", userId),
        where("receiverUid", "==", currentUserState.uid)
      );

      const [querySnapshot1, querySnapshot2] = await Promise.all([
        getDocs(sharedQuizzesQuery1),
        getDocs(sharedQuizzesQuery2),
      ]);

      const allDocs = [...querySnapshot1.docs, ...querySnapshot2.docs];
      console.log("🔍 Found shared quizzes between users:", allDocs.length);

      for (const docSnapshot of allDocs) {
        const sharedData = docSnapshot.data();
        console.log("🔍 Checking shared quiz:", {
          sharedId: docSnapshot.id,
          quizName: sharedData.quizName,
          senderUid: sharedData.senderUid,
          receiverUid: sharedData.receiverUid,
          senderQuizId: sharedData.senderQuizId,
          receiverQuizId: sharedData.receiverQuizId,
        });

        // Check if this shared quiz matches the quiz type and quiz IDs
        const isMatch =
          sharedData.quizName === config.quizName &&
          ((sharedData.senderQuizId === currentUserQuizId &&
            sharedData.receiverQuizId === profileUserQuizId) ||
            (sharedData.senderQuizId === profileUserQuizId &&
              sharedData.receiverQuizId === currentUserQuizId));

        console.log("🔍 Match check:", {
          quizNameMatch: sharedData.quizName === config.quizName,
          quizIdMatch:
            (sharedData.senderQuizId === currentUserQuizId &&
              sharedData.receiverQuizId === profileUserQuizId) ||
            (sharedData.senderQuizId === profileUserQuizId &&
              sharedData.receiverQuizId === currentUserQuizId),
          isMatch,
        });

        if (isMatch) {
          console.log("✅ Found matching shared quiz:", docSnapshot.id);
          return docSnapshot.id;
        }
      }

      console.log("❌ No matching shared quiz found");
      return null;
    } catch (error) {
      console.error("Error checking existing shared quiz:", error);
      return null;
    }
  };

  // Component to render trait slider
  const renderTraitSlider = (traitKey, percentage) => {
    const config = traitConfig[traitKey];
    if (!config) return null;

    return (
      <SliderContainer>
        <SliderTitle>{config.title}</SliderTitle>
        <SliderTrack>
          <SliderFill $percentage={percentage} />
        </SliderTrack>
        <SliderLabels>
          <SliderLabel>{config.leftLabel}</SliderLabel>
          <SliderLabel>{config.rightLabel}</SliderLabel>
        </SliderLabels>
      </SliderContainer>
    );
  };

  // Fetch and process quiz data
  const fetchSummaryData = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);

      // OPTIMIZED: Check cache first for user profile
      let userData = dataCache.getUserProfile(userId);

      if (!userData) {
        // Only fetch from Firebase if not cached
        const userProfileRef = doc(db, "public-profiles", userId);
        const userProfileDoc = await getDoc(userProfileRef);

        if (!userProfileDoc.exists()) {
          setLoading(false);
          return;
        }

        userData = userProfileDoc.data();
        // Cache for future use
        dataCache.setUserProfile(userId, userData);
      }
      const processedData = {};
      let completedCount = 0;
      const available = [];

      // Get all quiz configs from remote
      const allQuizConfigs = await remoteQuizService.getAllQuizConfigs();
      if (!allQuizConfigs) {
        console.warn("Could not fetch quiz configs");
        return;
      }

      // Store merged configs for rendering
      setMergedQuizConfigs(allQuizConfigs);

      // Process each quiz type
      for (const [quizType, config] of Object.entries(allQuizConfigs)) {
        const quizId = userData[config.databaseField];

        if (quizId) {
          try {
            // OPTIMIZED: Check cache for quiz data
            const quizCacheKey = `quiz-data-${quizId}`;
            let quizDocData = dataCache.get(quizCacheKey);

            if (!quizDocData) {
              const quizDocRef = doc(db, "quiz-answers", quizId);
              const quizDoc = await getDoc(quizDocRef);
              if (quizDoc.exists()) {
                quizDocData = quizDoc.data();
                // Cache quiz data for 10 minutes
                dataCache.set(quizCacheKey, quizDocData, 10 * 60 * 1000);
              }
            }

            if (quizDocData && quizDocData.status === "completed") {
              completedCount++;
              const quizAnswers = quizDocData.answers;

              // Convert answers to results with percentages
              // Need to average percentages for fields with multiple questions
              const fieldPercentages = {};
              Object.values(quizAnswers).forEach((answer) => {
                if (answer.field && typeof answer.percentage === "number") {
                  if (!fieldPercentages[answer.field]) {
                    fieldPercentages[answer.field] = [];
                  }
                  fieldPercentages[answer.field].push(answer.percentage);
                }
              });

              // Calculate average percentage for each field
              const results = {};
              Object.entries(fieldPercentages).forEach(
                ([field, percentages]) => {
                  const average =
                    percentages.reduce((sum, percent) => sum + percent, 0) /
                    percentages.length;
                  results[field] = Math.round(average);
                }
              );

              processedData[quizType] = results;
              available.push({ quizType, quizId });
            }
          } catch (err) {
            // Silently fail - individual quiz data is optional
          }
        }
      }

      // Update completed quiz count
      setCompletedQuizCount(completedCount);
      setAvailableQuizzes(available);

      // Build summary traits with raw percentages
      const summary = {};

      // From personality-check quiz
      if (processedData["personality-check"]) {
        const personality = processedData["personality-check"];
        if (personality.introversion !== undefined) {
          summary.socialEnergy = 100 - personality.introversion;
        }
        if (personality.feeling !== undefined) {
          summary.decisionDriver = personality.feeling;
        }
        if (personality.intuition !== undefined) {
          summary.mindset = personality.intuition;
        }
      }

      // From attachment-styles quiz
      if (processedData["attachment-styles"]) {
        const attachment = processedData["attachment-styles"];

        // Calculate anxious vs avoidant percentage
        // Higher anxious score = more anxious (left), higher avoidant = more avoidant (right)
        const anxiousScore = attachment.anxious || 50;
        const avoidantScore = attachment.avoidant || 50;

        // Calculate position: 0 = fully anxious, 100 = fully avoidant
        // We'll use the relative difference between the two scores
        const total = anxiousScore + avoidantScore;
        const attachmentPercentage =
          total > 0 ? (avoidantScore / total) * 100 : 50;

        summary.attachmentStyle = Math.round(attachmentPercentage);
      }

      // From political-alignment quiz
      if (processedData["political-alignment"]) {
        const political = processedData["political-alignment"];

        // Calculate average of all political fields
        const politicalFields = [
          "socialLib",
          "econLib",
          "globalism",
          "conservation",
          "public",
          "socialSupport",
        ];

        const validScores = politicalFields
          .map((field) => political[field])
          .filter((score) => score !== undefined);

        if (validScores.length > 0) {
          const average =
            validScores.reduce((sum, score) => sum + score, 0) /
            validScores.length;
          summary.politics = Math.round(average);
        }
      }

      // From best-date quiz
      if (processedData["best-date"]) {
        const dating = processedData["best-date"];
        if (dating.open !== undefined) {
          summary.connectionType = 100 - dating.open;
        }
        if (dating.planner !== undefined) {
          summary.structure = dating.planner;
        }
        if (dating.boujee !== undefined) {
          summary.lifestyle = dating.boujee;
        }
      }

      setSummaryData(summary);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Handle regenerate button click
  const handleRegenerate = useCallback(async () => {
    if (profileInsightsRef.current && !isRegenerating) {
      setIsRegenerating(true);

      // Track profile regeneration
      const regenerateProps = {
        user_id: currentUserState?.uid,
        is_public_profile: isPublicProfile,
        profile_user_id: userId,
        completed_quiz_count: completedQuizCount,
        timestamp: new Date().toISOString(),
      };
      mixpanelService.track("Profile Vibe Regenerated", regenerateProps);
      postHogService.track("Profile Vibe Regenerated", regenerateProps);

      try {
        await profileInsightsRef.current.regenerateInsights();
      } catch (error) {
      } finally {
        setIsRegenerating(false);
      }
    }
  }, [
    isRegenerating,
    profileInsightsRef,
    currentUserState,
    isPublicProfile,
    userId,
    completedQuizCount,
  ]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  // Expose regenerate functionality to parent component
  useEffect(() => {
    if (onRegenerateRef) {
      onRegenerateRef.current = {
        handleRegenerate,
        isRegenerating,
        completedQuizCount,
        profileInsightsRef,
      };
    }

    // Also notify parent component of data changes
    if (onRegenerateDataChange) {
      onRegenerateDataChange({
        handleRegenerate,
        isRegenerating,
        completedQuizCount,
        profileInsightsRef,
      });
    }
  }, [
    onRegenerateRef,
    isRegenerating,
    completedQuizCount,
    handleRegenerate,
    onRegenerateDataChange,
    profileInsightsRef,
  ]);

  const renderSummaryContent = () => {
    if (loading) {
      return (
        <LoadingRow>
          <P2>Loading personality data...</P2>
        </LoadingRow>
      );
    }

    // Define the order we want traits to appear
    const traitOrder = [
      "socialEnergy",
      "decisionDriver",
      "mindset",
      "attachmentStyle",
      "politics",
      "connectionType",
      "structure",
      "lifestyle",
    ];

    return (
      <>
        {summaryData &&
          Object.keys(summaryData).length > 0 &&
          traitOrder.map((key) => {
            if (summaryData[key] !== undefined) {
              return renderTraitSlider(key, summaryData[key]);
            }
            return null;
          })}
      </>
    );
  };

  // Handle quiz card click - check for existing shared quiz first
  const handleQuizCardClick = async (quizType, quizId) => {
    console.log("🔍 Quiz card clicked:", {
      quizType,
      quizId,
      currentUser: currentUserState?.uid,
      profileUser: userId,
    });

    // If viewing own profile, navigate directly to own results
    if (currentUserState?.uid === userId) {
      const config = mergedQuizConfigs[quizType];
      console.log("✅ Viewing own profile, navigating to own results");
      navigate(`${config.resultsRoute}?id=${quizId}`);
      return;
    }

    if (!currentUserState?.uid) {
      console.log("❌ No current user, proceeding to normal flow");
      // If user is not logged in, proceed to normal quiz flow
      navigate(`/${quizType}?from=${quizId}`);
      return;
    }

    try {
      // Check if a shared quiz already exists between current user and profile user
      console.log("🔍 Checking for existing shared quiz...");
      const existingSharedQuizId = await checkExistingSharedQuiz(
        quizType,
        quizId
      );
      console.log("🔍 Existing shared quiz ID:", existingSharedQuizId);

      if (existingSharedQuizId) {
        // Navigate directly to the existing shared quiz results
        const config = mergedQuizConfigs[quizType];
        console.log(
          "✅ Found existing shared quiz, navigating to results:",
          `${config.resultsRoute}?shared=${existingSharedQuizId}`
        );

        // Track viewing existing vibe check
        const vibeCheckViewProps = {
          quiz_type: quizType,
          quiz_name: config.title,
          viewer_id: currentUserState?.uid,
          profile_user_id: userId,
          shared_quiz_id: existingSharedQuizId,
          is_existing_comparison: true,
          timestamp: new Date().toISOString(),
        };
        mixpanelService.track("Vibe Check Viewed", vibeCheckViewProps);
        postHogService.track("Vibe Check Viewed", vibeCheckViewProps);

        navigate(`${config.resultsRoute}?shared=${existingSharedQuizId}`);
      } else {
        // No existing shared quiz, proceed with normal flow
        console.log(
          "❌ No existing shared quiz found, proceeding to normal flow"
        );

        // Track initiating new vibe check comparison
        const config = mergedQuizConfigs[quizType];
        const vibeCheckInitiateProps = {
          quiz_type: quizType,
          quiz_name: config?.title || quizType,
          viewer_id: currentUserState?.uid,
          profile_user_id: userId,
          quiz_id: quizId,
          is_existing_comparison: false,
          timestamp: new Date().toISOString(),
        };
        mixpanelService.track("Vibe Check Initiated", vibeCheckInitiateProps);
        postHogService.track("Vibe Check Initiated", vibeCheckInitiateProps);

        navigate(`/${quizType}?from=${quizId}`);
      }
    } catch (error) {
      console.error("Error handling quiz card click:", error);
      // Fallback to normal flow on error
      navigate(`/${quizType}?from=${quizId}`);
    }
  };

  // Overview content moved to Profile.js

  const renderQuizzesContent = () => {
    if (loading) {
      return (
        <LoadingRow>
          <P2>Loading quizzes...</P2>
        </LoadingRow>
      );
    }

    if (!availableQuizzes || availableQuizzes.length === 0) {
      return (
        <TraitRow>
          <P3 color={White08}>This user hasn't completed any quizzes yet</P3>
        </TraitRow>
      );
    }

    return (
      <QuizzesGrid>
        {availableQuizzes.map(({ quizType, quizId }) => {
          const colors =
            gradientColors[quizType] ||
            mergedQuizConfigs[quizType]?.gradientColors ||
            {};
          const config = mergedQuizConfigs[quizType] || {};
          const label =
            (config.results && config.results.titleText) ||
            config.titleWord ||
            quizType;
          return (
            <QuizCardBorder
              key={quizType}
              $c1={colors.background}
              $c2={colors.transitionBackground}
            >
              <QuizCardInner
                role="button"
                tabIndex={0}
                onClick={() => handleQuizCardClick(quizType, quizId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleQuizCardClick(quizType, quizId);
                  }
                }}
              >
                <QuizCardTitle>Take their {label} Vibe Check</QuizCardTitle>
              </QuizCardInner>
            </QuizCardBorder>
          );
        })}
      </QuizzesGrid>
    );
  };

  return (
    <SummaryContainer>
      <SummaryHeader>
        <TabContainer>
          <Tab
            $isActive={activeTab === "summary"}
            onClick={() => setActiveTab("summary")}
          >
            Summary
          </Tab>
          <Tab
            $isActive={activeTab === "quizzes"}
            onClick={() => setActiveTab("quizzes")}
          >
            Quizzes
          </Tab>
        </TabContainer>
      </SummaryHeader>

      <TabContent>
        {activeTab === "summary" && renderSummaryContent()}
        {activeTab === "quizzes" && renderQuizzesContent()}
      </TabContent>
    </SummaryContainer>
  );
};

export default Summary;
